import tinycolor from 'tinycolor2';

import {
  BRIGHT_COLORS,
  CHART_COLORS,
  INACTIVE_CHART_COLOR,
} from '../constants/colors';
import { NONE, UNKNOWN_CROP_NAME } from '../constants/fields';
import { getCustomLayerColor, layerIsCustom } from './layers';

export const cropThemes = {
  DARK: 'dark',
  LIGHT: 'light',
};

export const defaultCropColorForTheme = (theme) =>
  theme === cropThemes.DARK ? '#9B9A97' : '#9C9FA0'; // dark/light grey

const noCropColor = 'rgba(0,0,0,0.2)'; // Transparent black

// if crop.color doesn't exist, return undefined
// else, return its hex code
const cropColorForTheme = (crop, theme) =>
  crop?.color &&
  (theme === cropThemes.DARK
    ? `#${crop?.color?.hexDark}`
    : `#${crop?.color?.hexLight}`);

export const colorForCrop = (crop, theme = cropThemes.DARK) =>
  cropColorForTheme(crop, theme) || defaultCropColorForTheme(theme);

export const cropColorsMap = (crops, theme) => {
  const colorMap = {};
  crops.forEach((crop) => {
    colorMap[crop.name] = colorForCrop(crop, theme);
  });
  colorMap[NONE] = noCropColor;
  colorMap[UNKNOWN_CROP_NAME] = defaultCropColorForTheme(theme);
  return colorMap;
};

// if item is a crop, use its color
// otherwise, pick from CHART_COLORS based on index
export const getItemColor = (item, index, theme = cropThemes.DARK) =>
  ((c) =>
    c === defaultCropColorForTheme(theme)
      ? CHART_COLORS[index % CHART_COLORS.length]
      : c)(colorForCrop(item, theme));

// items could be crops
// or any other type of object
export const addItemColors = (items) =>
  items.map((item, index) => ({
    ...item,
    color: getItemColor(item, index),
  }));

export const getUnusedBrightColor = (colors) =>
  colors.length >= BRIGHT_COLORS.length
    ? BRIGHT_COLORS[colors.length % BRIGHT_COLORS.length]
    : BRIGHT_COLORS.find((c) => !colors.includes(c));

export const getPaintColor = (layer) => {
  if (layerIsCustom(layer)) return getCustomLayerColor(layer);

  const { paintProperties } = layer;
  const color =
    paintProperties.paint['line-color'] || paintProperties.paint['fill-color'];
  if (Array.isArray(color)) {
    // Handle basic match/case expressions by returning the default color
    const defaultMatchColor = color[color.length - 1];
    return typeof defaultMatchColor === 'string' &&
      ['match', 'case'].includes(color[0])
      ? defaultMatchColor
      : // Return black by default for unhandled cases
        '#000000';
  }
  return color;
};

// When there is an active item, show the INACTIVE_CHART_COLOR
// for inactive items
export const getChartItemColor = (id, activeKey, color) => {
  const isActive = activeKey === id;
  const showColor = isActive || activeKey === '';
  return showColor ? color : INACTIVE_CHART_COLOR;
};

export const MAP_ACTIVE_COLOR = '#03ecfc';

export const darkenColor = (color) => tinycolor(color).darken(25).toString();

export const getLightness = (color) => tinycolor(color).toHsl().l;

/**
 * Given a color and a hue gradient, returns the position (0 to 1) of the color in
 * the gradient, or null if the color is not in the gradient.
 */
export const getColorPositionInGradient = (colorString, gradientStrings) => {
  const color = tinycolor(colorString).toHsl();
  const gradient = gradientStrings.map((s) => tinycolor(s).toHsl());

  // We need to least 2 colors to make a gradient:
  if (gradient.length < 2) return null;

  // We need a defined color:
  if (!colorString || colorString.length === 0) return null;

  // see if any colors are a direct match
  const matchIndex = gradient.findIndex(
    (gradientColor) => gradientColor.h === color.h,
  );
  if (matchIndex !== -1) return matchIndex / (gradient.length - 1);

  // get the index of the left element of the pair that the color is between
  const leftIndex = gradient.slice(0, -1).findIndex((left, i) => {
    const right = gradient[i + 1];
    return left.h < color.h && color.h < right.h;
  });

  if (leftIndex === -1) return null; // no match

  const left = gradient[leftIndex];
  const right = gradient[leftIndex + 1];

  // Calculate the position of the color between the left and right colors
  const position = (color.h - left.h) / (right.h - left.h);

  // Return the position within the whole gradient, as a value between 0 and 1
  return (leftIndex + position) / (gradient.length - 1);
};

/**
 * This fn is the opposite of getColorPositionInGradient. Returns hue of the
 * gradient at a given position, on a 0-1 scale.
 */
export const getGradientHueAtPosition = (position, gradientStrings) => {
  const gradient = gradientStrings.map((s) => tinycolor(s).toHsl());

  // We need to least 2 colors to make a gradient:
  if (gradient.length < 2) return null;

  // We need a defined position:
  if (position === null || position < 0 || position > 1) return null;

  // get the index of the left element of the pair that the color is between
  const positionIndex = position * (gradient.length - 1);
  const leftIndex = Math.floor(positionIndex);

  const left = gradient[leftIndex];
  const right = gradient[leftIndex + 1];

  return (left.h + (right.h - left.h) * (positionIndex - leftIndex)) / 360;
};

export const isGrayscale = (colorString) =>
  tinycolor(colorString).toHsl().s === 0;

/**
 * Compute the perceptual difference between two colors using the Delta-E
 * approach. Returns a number between 0 and 100, where 0 means the colors are
 * identical.
 *
 * See:
 * - https://zschuessler.github.io/DeltaE/learn/
 * - https://github.com/antimatter15/rgb-lab
 */
export const getColorDifference = (colorA, colorB) => {
  const getRgb = (colorString) => {
    const { r, g, b } = tinycolor(colorString).toRgb();
    return [r, g, b];
  };

  const rgbA = getRgb(colorA);
  const rgbB = getRgb(colorB);

  const rgb2lab = (rgb) => {
    let r = rgb[0] / 255;
    let g = rgb[1] / 255;
    let b = rgb[2] / 255;

    let x;
    let y;
    let z;

    r = r > 0.04045 ? (r + 0.055) / 1.055 ** 2.4 : r / 12.92;
    g = g > 0.04045 ? (g + 0.055) / 1.055 ** 2.4 : g / 12.92;
    b = b > 0.04045 ? (b + 0.055) / 1.055 ** 2.4 : b / 12.92;

    x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
    y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.0;
    z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;

    x = x > 0.008856 ? x ** (1 / 3) : 7.787 * x + 16 / 116;
    y = y > 0.008856 ? y ** (1 / 3) : 7.787 * y + 16 / 116;
    z = z > 0.008856 ? z ** (1 / 3) : 7.787 * z + 16 / 116;

    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  };

  const labA = rgb2lab(rgbA);
  const labB = rgb2lab(rgbB);

  const deltaL = labA[0] - labB[0];
  const deltaA = labA[1] - labB[1];
  const deltaB = labA[2] - labB[2];

  const c1 = Math.sqrt(labA[1] * labA[1] + labA[2] * labA[2]);
  const c2 = Math.sqrt(labB[1] * labB[1] + labB[2] * labB[2]);
  const deltaC = c1 - c2;

  let deltaH = deltaA * deltaA + deltaB * deltaB - deltaC * deltaC;
  deltaH = deltaH < 0 ? 0 : Math.sqrt(deltaH);

  const sc = 1.0 + 0.045 * c1;
  const sh = 1.0 + 0.015 * c1;

  const deltaLKlsl = deltaL / 1.0;
  const deltaCkcsc = deltaC / sc;
  const deltaHkhsh = deltaH / sh;

  const i =
    deltaLKlsl * deltaLKlsl + deltaCkcsc * deltaCkcsc + deltaHkhsh * deltaHkhsh;

  return i < 0 ? 0 : Math.sqrt(i);
};
